import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { selectUnits } from 'shared/slice/unitsSlice';
import { selectTelemetry } from 'features/telemetry/slice/telemetrySlice';
import {
  borderBlue,
  flexBoxCenter,
  justifyContentSpaceBetween,
  layerADark,
  layerBDark,
} from 'shared/styles/commonStyles';
import SelectedSwitch from './SelectedSwitch';
import SelectedSpecificLocation from './SelectedSpecificLocation';
import { useLocations } from 'shared/hooks';

const getMachineId = (machineValues) =>
  machineValues?.deviceMac ||
  machineValues?.machineMac ||
  machineValues?.device_mac;

const isMachineRecord = (value) =>
  Boolean(
    value &&
      typeof value === 'object' &&
      (value.isSelected !== undefined ||
        value.machineName ||
        value.deviceName ||
        getMachineId(value))
  );

const getSwitchDisplayName = (machineValues) => {
  const locationName = machineValues?.locationName || machineValues?.location_id || '-';
  const machineName =
    machineValues?.machineName ||
    machineValues?.deviceName ||
    getMachineId(machineValues) ||
    '-';

  return `${locationName} - ${machineName}`;
};

const getGroupMachines = (value) =>
  Object.values(value?.devices || value || {}).filter(
    (machine) => machine && typeof machine === 'object' && isMachineRecord(machine)
  );

const SwitchSpecification = ({
  systemSwitches,
  isExpand,
  src,
  system,
  handleExpand,
  inputSearch,
  handleClearSearch,
  setSelectedLocations,
  selectedLocations,
  specificLocationsOpeningControl,
  setSpecificLocationsOpeningControl,
  totalDataConsumpOrEnergyConsump,
  setTotalDataConsumpOrEnergyConsump,
  totalUsageHours,
  setTotalUsageHours,
  totalNumOfSwt,
  setTotalNumOfSwt,
  swtName,
  telemetryData,
  isDataChanged,
  isDc,
  searchedSwtName,
  setSearchedSwtName,
  setIsExpand,
  selectSpecific,
}) => {
  const telemetryState = useSelector(selectTelemetry);
  const isSearch = telemetryState.isSearch;
  const settingsUnitsState = useSelector(selectUnits);
  const { isF } = settingsUnitsState;
  const locations = useLocations();

  const energySign =
    systemSwitches &&
    (systemSwitches[0][5] === 'ess' || systemSwitches[0][5] === 'tes'
      ? 'kw'
      : systemSwitches[0][5] === 'tgs'
      ? isF
        ? 'ft³'
        : 'm³'
      : 'mb');

  const expandSpecificLocationHandler = useCallback(
    (locationIdx, specificLocationIdx) => {
      const copyIsExpand = [...specificLocationsOpeningControl];
      if (copyIsExpand[locationIdx][specificLocationIdx]) {
        copyIsExpand[locationIdx][specificLocationIdx] = false;
        setSpecificLocationsOpeningControl(copyIsExpand);
      } else {
        copyIsExpand[locationIdx][specificLocationIdx] = true;
        setSpecificLocationsOpeningControl(copyIsExpand);
      }
      setSearchedSwtName('');
    },
    [
      specificLocationsOpeningControl,
      setSpecificLocationsOpeningControl,
      setSearchedSwtName,
    ]
  );

  const findConsumptionValue = (machine, totalConsumption) => {
    return (
      Number(
        telemetryData?.find((el) => el.machine === machine)?.[totalConsumption]
      ) || 0
    );
  };

  useEffect(() => {
    if (system) {
      const copyTotalDataConsumpOrEnergyConsump = [
        ...totalDataConsumpOrEnergyConsump,
      ];
      const copyTotalUsageHours = [...totalUsageHours];
      const copySelectedLocations = [...selectedLocations];
      const copyTotalNumOfSwt = [...totalNumOfSwt];

      Object.values(system)?.forEach((machine, index) => {


        const machineArr = Object.values(machine);

        const checkedMachines = machineArr.some(
          (machineValues) => machineValues?.isSelected
        );

        const checkedSpecificLocationMachines = machineArr.some((el) =>
          Object.values(el).some((machineValues) => machineValues?.isSelected)
        );

        if (checkedMachines) {
          copySelectedLocations[index] = true;
        } else if (checkedSpecificLocationMachines) {
          copySelectedLocations[index] = true;
        }












        let sumOfDataConsumpOrEnergyConsump = 0;
        let sumOfUsageHours = 0;
        let sumOfNumOfSwt = 0;
        machineArr.forEach((machineValues) => {
          const totalConsumption = 'totalConsumption';
          const totalUsageHours = 'totalUsageHours';
          if (machineValues.isSelected) {
            const machineKey = getMachineId(machineValues);
            sumOfDataConsumpOrEnergyConsump += findConsumptionValue(
              machineKey,
              totalConsumption
            );
            sumOfUsageHours += findConsumptionValue(
              machineKey,
              totalUsageHours
            );
            sumOfNumOfSwt++;
          } else if (!isMachineRecord(machineValues) && Object.keys(machineValues).length > 0) {

            if (Object.values(machineValues).filter((el) => el?.isSelected)) {
              Object.values(machineValues).forEach((elData) => {

                const machineKey = getMachineId(elData);
                sumOfDataConsumpOrEnergyConsump += findConsumptionValue(
                  machineKey,
                  totalConsumption
                );
                sumOfUsageHours += findConsumptionValue(
                  machineKey,
                  totalUsageHours
                );
                sumOfNumOfSwt++;
              });
            }
          }
        });
        copyTotalDataConsumpOrEnergyConsump[index] =
          sumOfDataConsumpOrEnergyConsump;
        copyTotalUsageHours[index] = sumOfUsageHours;
        copyTotalNumOfSwt[index] = sumOfNumOfSwt;
      });

      setSelectedLocations(copySelectedLocations);
      setTotalDataConsumpOrEnergyConsump(copyTotalDataConsumpOrEnergyConsump);
      setTotalUsageHours(copyTotalUsageHours);
      setTotalNumOfSwt(copyTotalNumOfSwt);
    }
  }, [system, telemetryData, isDataChanged]);

  const verificationOfDataConsump =
    systemSwitches &&
    (systemSwitches[0][5] === 'essDc' ||
      systemSwitches[0][5] === 'tgsDc' ||
      systemSwitches[0][5] === 'tesDc' ||
      systemSwitches[0][5] === 'hpDc');

  return (
    <Wrapper>
      {isSearch && systemSwitches
        ? systemSwitches[0][4]?.map((location, index) => {
            if (selectedLocations[index]) {
              const systemLocations =
                locations[
                  swtName === 'tgs/tes' ? swtName.slice(0, 3) : swtName
                ];
              const locationName = systemLocations?.[location]?.locationName || '-';

              return (
                <FlexWrapper key={location}>
                  <IndentTitle>
                    <TitleWrapper>
                      <Title>
                        switch <br /> specification
                      </Title>

                      {isDc ? (
                        ''
                      ) : (
                        <Title>
                          usage <br></br> hours
                        </Title>
                      )}

                      <Title>
                        {verificationOfDataConsump ? (
                          <>
                            data <br></br> consumption
                          </>
                        ) : swtName === 'tgs' ? (
                          <>
                            gas <br></br> consumption
                          </>
                        ) : (
                          <>
                            energy <br></br> consumption
                          </>
                        )}
                      </Title>
                    </TitleWrapper>
                  </IndentTitle>
                  <LocationWrapper>
                    <LocationWrapper1>
                      <LocationWrapper2>
                        <FlexDiv>
                          <ImgLocationWrapper>
                            <Img
                              src={src[index]}



                              onClick={() => handleExpand(index)}
                            />
                            <P1>{locationName}</P1>
                          </ImgLocationWrapper>
                          <Info isSwitches={true}>
                            {totalNumOfSwt[index] || '-'} switches
                          </Info>
                          {isDc ? (
                            ''
                          ) : (
                            <Info isHours={true}>
                              {totalUsageHours[index]
                                ? totalUsageHours[index]
                                : '-'}{' '}
                              Hrs
                            </Info>
                          )}
                          <Info isConsumption={true}>
                            {isDc
                              ? totalDataConsumpOrEnergyConsump[index]
                                ? (
                                    totalDataConsumpOrEnergyConsump[index] /
                                    (1024 * 1024)
                                  ).toFixed(2)
                                : '-'
                              : totalDataConsumpOrEnergyConsump[index]
                              ? totalDataConsumpOrEnergyConsump[index]
                              : '-'}{' '}
                            {energySign}
                          </Info>
                        </FlexDiv>
                      </LocationWrapper2>
                      {isExpand[index] &&
                        Object.entries(system[location]).map(
                          ([key, value], idx) => {
                            if (value.isSelected) {
                              return (
                                <SelectedSwitch
                                  key={idx}
                                  switchData={getSwitchDisplayName(value)}
                                  energySymbol={energySign}
                                  machineValues={value}
                                  telemetryData={telemetryData}
                                  isDc={isDc}
                                  selectSpecific={selectSpecific}
                                />
                              );
                            }

                            const groupMachines = getGroupMachines(value);
                            if (!groupMachines.length) return null;

                            return (
                              <SelectedSpecificLocation
                                key={idx}
                                locationIdx={index}
                                specificLocationIdx={idx}
                                specificLocationValues={groupMachines}
                                energySymbol={energySign}
                                telemetryData={telemetryData}
                                isDc={isDc}
                                specificLocationsOpeningControl={
                                  specificLocationsOpeningControl
                                }
                                expandSpecificLocationHandler={
                                  expandSpecificLocationHandler
                                }
                              />
                            );
                          }
                        )}


                      {searchedSwtName[0]?.slice(
                        0,
                        searchedSwtName[0].indexOf(' - ')
                      ) === location &&
                        !isExpand[index] &&
                        Object.entries(
                          system[searchedSwtName[0]?.split(' - ')[0]] || {}
                        ).map(([machine, value], idx) => {
                          const searchedParts = searchedSwtName[0].split(' - ');
                          const searchedMachine =
                            searchedParts[searchedParts.length - 1];
                          if (
                            machine ===
                            searchedMachine
                          ) {
                            return (
                              <SelectedSwitch
                                key={idx}
                                switchData={getSwitchDisplayName(value)}
                                energySymbol={energySign}
                                machineValues={value}
                                telemetryData={telemetryData}
                                isDc={isDc}
                                selectSpecific={selectSpecific}
                              />
                            );
                          }

                          const groupMachines = getGroupMachines(value).filter(
                            (machineValues) =>
                              getMachineId(machineValues) === searchedMachine ||
                              machineValues.machineName === searchedMachine ||
                              machineValues.deviceName === searchedMachine
                          );

                          if (!groupMachines.length) return null;

                          return (
                            <SelectedSpecificLocation
                              key={idx}
                              locationIdx={index}
                              specificLocationIdx={idx}
                              specificLocationValues={groupMachines}
                              energySymbol={energySign}
                              telemetryData={telemetryData}
                              isDc={isDc}
                              specificLocationsOpeningControl={
                                specificLocationsOpeningControl
                              }
                              expandSpecificLocationHandler={
                                expandSpecificLocationHandler
                              }
                              isSearched={true}
                            />
                          );
                        })}

                    </LocationWrapper1>
                  </LocationWrapper>
                </FlexWrapper>
              );
            }
          })
        : [' ', ' ']?.map((_, index) => {
            return (
              <FlexWrapper key={index}>
                <IndentTitle>
                  <TitleWrapper>
                    <Title>
                      switch <br></br> specification
                    </Title>
                    <Title>
                      usage <br></br> hours
                    </Title>
                    <Title>
                      {verificationOfDataConsump ? (
                        <>
                          data <br></br> consumption
                        </>
                      ) : swtName === 'tgs' ? (
                        <>
                          gas <br></br> consumption
                        </>
                      ) : (
                        <>
                          energy <br></br> consumption
                        </>
                      )}
                    </Title>
                  </TitleWrapper>
                </IndentTitle>
              </FlexWrapper>
            );
          })}
    </Wrapper>
  );
};

export default SwitchSpecification;

const Wrapper = styled.div`
  width: 1198px;
  height: auto;
  margin-top: 8px;
  margin-bottom: 8px;

  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 6px;
  flex-direction: row;
  flex-wrap: wrap;
`;

const FlexWrapper = styled.div`
  ${flexBoxCenter}
  flex-direction: column;
  flex-wrap: wrap;
`;

const IndentTitle = styled.div`
  width: 592px;
  height: 27px;
  margin-bottom: 4px;

  ${layerADark}

  border-radius: 14px;
  opacity: 1;
  ${flexBoxCenter}
`;

const TitleWrapper = styled.div`
  width: 571px;
  height: auto;

  border-bottom: 1px solid #ffff;
  ${justifyContentSpaceBetween}
`;

const Title = styled.span`
  &:nth-child(2) {
    margin-right: 53px;
  }
  text-align: left;
  font-size: 8px;
  letter-spacing: 0.8px;
  color: #ffffff;
  opacity: 1;
  margin-right: 8px;
  &:nth-child(2) {
    margin-left: 230px;
  }
`;

const LocationWrapper = styled.div`
  width: 592px;
  height: auto;

  ${layerBDark}

  border-radius: 16px;
  opacity: 1;

  ${flexBoxCenter}
`;

const LocationWrapper1 = styled.div`
  width: 590px;
  height: auto;

  ${borderBlue}

  border-radius: 15px;
  opacity: 1;
  ${flexBoxCenter};
  flex-direction: column;
`;

const LocationWrapper2 = styled.div`
  width: 99.4%;
  height: 26px;
  margin-top: 2px;
  margin-bottom: 2px;

  ${borderBlue}

  border-radius: 14px;
  ${flexBoxCenter}
`;

const FlexDiv = styled.div`
  width: 99%;
  height: 100%;
  ${justifyContentSpaceBetween};
  position: relative;
`;

const ImgLocationWrapper = styled.div`
  width: fit-content;
  ${justifyContentSpaceBetween}
`;

const Img = styled.img`
  margin-left: 8px;
  margin-right: 4px;
  cursor: pointer;
`;

const P1 = styled.span`
  width: fit-content;
  text-align: left;
  font-size: 9px;
  letter-spacing: 0.9px;
  color: #ffffff;
  opacity: 1;
`;

const Info = styled.span`
  font-size: 9px;
  width: fit-content;

  position: absolute;
  ${({ isConsumption, isHours }) =>
    isConsumption
      ? css`
          top: 7px;
          left: 88%;
        `
      : isHours
      ? css`
          top: 7px;
          left: 62%;
        `
      : css`
          top: 7px;
          left: 34%;
        `}
`;
